import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'
import { useToast } from '@/stores/toast'

interface FormWithId { id?: number }

interface Opts<TForm extends FormWithId, TSaved = TForm> {
  /** API base URL — e.g. '/api/items', '/api/partners' */
  apiBase: string
  /** invalidate 대상 queryKey prefix — e.g. 'items', 'partners' */
  queryKey: string | readonly unknown[]
  /** 저장/삭제 시 추가로 invalidate 할 queryKey 목록 (연관 데이터 갱신용) */
  invalidateAlso?: (string | readonly unknown[])[]
  /** 저장 성공 후 실행 (보통 setEdit(null) 등 모달 닫기) */
  onSaved?: (data: TSaved) => void
  /** 삭제 성공 후 (기본: 아무 것도 안 함) */
  onRemoved?: () => void
  /** URL 마지막에 '/' 붙일지 (DRF trailing_slash=true 인 라우터용). 기본 false. */
  trailingSlash?: boolean
  /** 저장 성공 토스트. false 면 비활성. 기본 '저장됨'. */
  saveToast?: string | false
  /** 삭제 성공 토스트. false 면 비활성. 기본 없음(무음). */
  deleteToast?: string | false
}

/** 마스터 CRUD 페이지 공용 save/remove mutation 세트.
 *
 *  사용:
 *    const { save, remove } = useCrudMutations<ItemForm, Item>({
 *      apiBase: '/api/items',
 *      queryKey: 'items',
 *      onSaved: () => setEdit(null),
 *    })
 *    save.mutate(edit)
 *    remove.mutate(id)
 *
 *  Detail 엔드포인트는 `/${apiBase}/${id}` 로 가정 (DRF trailing_slash=false 호환).
 */
export function useCrudMutations<TForm extends FormWithId, TSaved = TForm>(
  opts: Opts<TForm, TSaved>,
) {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const keyArr = typeof opts.queryKey === 'string' ? [opts.queryKey] : opts.queryKey
  const alsoKeys = (opts.invalidateAlso ?? []).map((k) =>
    typeof k === 'string' ? [k] : k)
  const savedMsg = opts.saveToast === undefined ? '저장됨' : opts.saveToast
  const deletedMsg = opts.deleteToast === undefined ? '' : opts.deleteToast
  const slash = opts.trailingSlash ? '/' : ''
  function invalidateAll() {
    qc.invalidateQueries({ queryKey: keyArr })
    for (const k of alsoKeys) qc.invalidateQueries({ queryKey: k })
  }

  const save = useMutation({
    mutationFn: (f: TForm) =>
      f.id
        ? api<TSaved>(`${opts.apiBase}/${f.id}${slash}`, { method: 'PATCH', body: f })
        : api<TSaved>(`${opts.apiBase}${slash}`, { method: 'POST', body: f }),
    onSuccess: (data) => {
      invalidateAll()
      opts.onSaved?.(data)
      if (savedMsg) push(savedMsg, 'success')
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 5000),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api(`${opts.apiBase}/${id}${slash}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidateAll()
      opts.onRemoved?.()
      if (deletedMsg) push(deletedMsg, 'success')
    },
    onError: (e) => push(`삭제 실패: ${e.message}`, 'error', 5000),
  })

  return { save, remove }
}
